import {
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

export type TableDensity = 'default' | 'dense';

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
}

export function Table({ density = 'default', className, ...rest }: TableProps) {
  return (
    <table
      data-density={density}
      className={cn(
        'phx-table text-phx-md text-phx-text w-full border-separate border-spacing-0',
        className
      )}
      {...rest}
    />
  );
}

const HEAD_DENSITY: Record<TableDensity, string> = {
  default: 'h-phx-row px-phx-4_5 text-phx-xs',
  dense: 'h-phx-row-dense px-phx-4 text-phx-sm',
};

const CELL_DENSITY: Record<TableDensity, string> = {
  default: 'h-phx-row px-phx-4_5',
  dense: 'h-phx-row-dense px-phx-4 text-phx-sm',
};

export interface TableHeadCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  density?: TableDensity;
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;
}

export function TableHeadCell({
  density = 'default',
  sortable,
  sortDirection,
  className,
  children,
  ...rest
}: TableHeadCellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'border-phx-border bg-phx-surface-2 border-b text-left font-semibold uppercase',
        'text-phx-text-muted tracking-wider whitespace-nowrap',
        HEAD_DENSITY[density],
        className
      )}
      {...rest}
    >
      {sortable ? (
        <span className="phx-th-sortable gap-phx-2 inline-flex cursor-pointer items-center select-none">
          {children}
          <Icon
            name={
              sortDirection === 'asc' ? 'chevup' : sortDirection === 'desc' ? 'chevdown' : 'sort'
            }
            size={11}
          />
        </span>
      ) : (
        children
      )}
    </th>
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  emphasis?: 'strong';
}

export function TableRow({ selected, emphasis, className, ...rest }: TableRowProps) {
  return (
    <tr
      data-selected={selected ? '' : undefined}
      data-emphasis={emphasis}
      className={cn(
        'phx-table-row [&>td]:bg-transparent hover:[&>td]:bg-[rgba(168,57,43,0.04)]',
        selected && '[&>td]:bg-phx-primary-tint hover:[&>td]:bg-phx-primary-tint',
        emphasis === 'strong' && '[&>td]:bg-phx-surface-2 [&>td]:font-semibold',
        className
      )}
      {...rest}
    />
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  density?: TableDensity;
}

export function TableCell({ density = 'default', className, ...rest }: TableCellProps) {
  return (
    <td
      className={cn(
        'border-phx-border text-phx-text border-b align-middle',
        CELL_DENSITY[density],
        className
      )}
      {...rest}
    />
  );
}

Table.Head = function Head({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...rest}>{children}</thead>;
};
Table.Body = function Body({ children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...rest}>{children}</tbody>;
};
Table.HCell = TableHeadCell;
Table.Row = TableRow;
Table.Cell = TableCell;
